import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2 } from 'lucide-react'

interface UserProfileStepProps {
  onSubmit: (data: Record<string, unknown>) => void
  disabled?: boolean
}

const ROLES = [
  'Marketing',
  'Sales',
  'Operations',
  'Finance',
  'Legal',
  'HR',
  'Engineering',
  'Product',
  'Design',
  'Executive',
  'Other'
]

const COMPANY_SIZES = [
  { value: '1', label: 'Just me' },
  { value: '2-10', label: '2-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-1000', label: '201-1000 employees' },
  { value: '1000+', label: '1000+ employees' }
]

export function UserProfileStep({ onSubmit, disabled }: UserProfileStepProps) {
  const [formData, setFormData] = useState({
    userName: '',
    companyName: '',
    role: '',
    companySize: ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: Record<string, string> = {}

    if (!formData.userName.trim()) {
      newErrors.userName = 'Name is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    onSubmit(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-slate-900 mb-2">
          Tell us about yourself
        </h2>
        <p className="text-slate-600">
          This helps us personalize your experience
        </p>
      </div>

      <div className="space-y-4 max-w-md mx-auto">
        <div className="space-y-2">
          <Label htmlFor="userName">Your name *</Label>
          <Input
            id="userName"
            value={formData.userName}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, userName: e.target.value }))
              setErrors(prev => ({ ...prev, userName: '' }))
            }}
            placeholder="Enter your name"
            className={errors.userName ? 'border-destructive' : ''}
          />
          {errors.userName && (
            <p className="text-sm text-destructive">{errors.userName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyName">Company name</Label>
          <Input
            id="companyName"
            value={formData.companyName}
            onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
            placeholder="Enter your company name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Your role</Label>
          <Select
            value={formData.role}
            onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
          >
            <SelectTrigger id="role">
              <SelectValue placeholder="Select your role" />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="companySize">Company size</Label>
          <Select
            value={formData.companySize}
            onValueChange={(value) => setFormData(prev => ({ ...prev, companySize: value }))}
          >
            <SelectTrigger id="companySize">
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_SIZES.map((size) => (
                <SelectItem key={size.value} value={size.value}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-center pt-4">
        <Button type="submit" disabled={disabled} size="lg">
          {disabled ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </form>
  )
}
